import { subscriptionApiAxiosInstance } from '@/helpers/request'
import isSameDay from 'date-fns/isSameDay'
import parseISO from 'date-fns/parseISO'

const DOWNGRADE_PRODUCTS_MAP = {
	prd_nGj0qIOxEggu: 'prd_ktsg3725cCqj',
	prd_4NTYz5Tu3L4K: 'prd_zwnL1Jn4ngIJ',
	prd_sbY0NWq9GMkz: 'prd_zwnL1Jn4ngIJ',
	prd_kvFfWmOs3D4D: 'prd_Wt8CBPPqAsYR',
	prd_EYnNTjPZUV4P: 'prd_eDxdOig8hIKC',
	prd_WENKtLRaJui0: 'prd_eDxdOig8hIKC',
}

export function findDowngradeProduct(id) {
	return DOWNGRADE_PRODUCTS_MAP[normalizeProductId(id)] ?? null
}

function normalizeProductId(id) {
	return id.replace(/^(.{9}).*(.{7})$/, '$1$2')
}

function isDowngradedProduct(id) {
	return Boolean(id) && Object.values(DOWNGRADE_PRODUCTS_MAP).includes(normalizeProductId(id))
}

export default {
	namespaced: true,

	state: {
		subscription: null,
	},

	mutations: {
		setSubscription(state, subscription) {
			if (!subscription) {
				return
			}

			if (subscription.scheduled) {
				if (isDowngradedProduct(subscription?.product_id) || isDowngradedProduct(subscription?.scheduled?.product_id)) {
					subscription.cancelled = false
					subscription.cancelled_at = null
					subscription.subscription_id = subscription.scheduled.subscription_id
				} else {
					subscription.scheduled.phases[0].is_active = true
					subscription = {
						...subscription,
						...subscription.scheduled,
					}
				}
			}
			state.subscription = subscription
		},
		subscriptionMarkCanceled(state) {
			state.subscription.cancelled = true
		},
	},

	getters: {
		hasSubscription: (state) => {
			return Boolean(state.subscription && Object.keys(state.subscription).length)
		},

		getSubscription: (state, getters) => {
			const phases = getters.getPhases
			const subscription = state.subscription

			if (!getters.hasSubscription) {
				return {}
			}

			return {
				...(phases.regular?.currency && { currency: phases.regular.currency }),
				...subscription,
				...phases,
			}
		},

		getCurrency: (state, getters) => {
			return getters.getSubscription?.currency?.toLowerCase?.()
		},

		getActivePhase: (state, getters) => {
			return Object.values(getters.getPhases).find((phase) => phase.is_active)
		},

		getPhases: (state) => {
			const phases = state.subscription?.phases ?? []

			return phases.reduce((map, phase) => {
				const iterationPrices = phase.iteration_prices ?? []
				const iterationPrice = iterationPrices.find(
					(price) => phase.is_active && price.iteration === state.subscription?.billing_period_number,
				)

				map[phase.phase_type] = {
					...phase,
					price: iterationPrice?.price ?? phase.price,
					discount: iterationPrice?.discount ?? null,
				}
				return map
			}, {})
		},

		checkPeriodType: (state, getters) => {
			const activePhase = getters.getActivePhase

			return {
				isCommitment: activePhase?.phase_type === 'commitment',
				isTrial: activePhase?.phase_type === 'trial',
				isRegular: activePhase?.phase_type === 'regular',
			}
		},

		checkProvider: (state, getters) => {
			const provider = getters.getSubscription?.provider

			return {
				isPalta: provider === 'palta',
				isStripe: provider === 'stripe',
				isBraintree: provider === 'braintree',
				isPrimer: provider === 'primer',
			}
		},

		checkStatus: (state, getters) => {
			return {
				isActive: Boolean(getters.hasSubscription && !state.subscription?.cancelled),
				isPaused: Boolean(state.subscription?.is_paused),
				isCancelled: Boolean(state.subscription?.cancelled),
				isExpired: Boolean(state.subscription?.is_expired),
				isOverdue: Boolean(state.subscription?.is_overdue),
				isDowngraded: isDowngradedProduct(state.subscription?.product_id),
				isPendingDowngrade: isDowngradedProduct(state.subscription?.scheduled?.product_id),
			}
		},

		isCancelledViaBreakContract: (state) => {
			const isCancelled = Boolean(state.subscription?.cancelled)
			const expiredAt = state.subscription?.expired_at
			const currentPeriodEndAt = state.subscription?.current_period_end_at

			if (!isCancelled || !expiredAt || !currentPeriodEndAt) {
				return false
			}

			return isSameDay(parseISO(expiredAt), parseISO(currentPeriodEndAt))
		},

		checkBillingIntervalType: (state, getters) => {
			return {
				isWeek: getters.getSubscription?.regular.interval_unit === 'week',
				isMonth: getters.getSubscription?.regular.interval_unit === 'month',
				isYear: getters.getSubscription?.regular.interval_unit === 'year',
				isDays: getters.getSubscription?.regular.interval_unit === 'days',
			}
		},

		isTrialEqual: (state, getters) => (duration, unit) => {
			return (
				getters.getSubscription?.trial?.interval_duration === duration &&
				getters.getSubscription?.trial?.interval_unit === unit
			)
		},

		getIsLegacy: (state, getters) => {
			return [getters.checkProvider.isStripe, getters.checkProvider.isBraintree].some(Boolean)
		},

		getIsModern: (state, getters) => {
			return [getters.checkProvider.isPalta, getters.checkProvider.isPrimer].some(Boolean)
		},

		// Can use features
		canUpgrade: (state, getters) => {
			return getters.getIsModern && !getters.checkPeriodType.isRegular
		},

		canPause: (state, getters) => {
			return getters.getIsModern && getters.getSubscription.is_pause_allowed
		},

		canResume: (state, getters) => {
			return getters.getSubscription.is_paused
		},

		canPurchaseCancelOffer: (state, getters) => {
			if (getters.getSubscription.payment_method === 'pix_automatico') {
				return false
			}

			return getters.getSubscription.is_cancel_offer_allowed
		},

		canDowngrade: (state, getters) => {
			if (!getters.hasSubscription || !getters.checkProvider.isPrimer || getters.checkStatus.isCancelled) {
				return false
			}

			const normalizedSubscriptionId = normalizeProductId(getters.getSubscription.product_id)

			return DOWNGRADE_PRODUCTS_MAP[normalizedSubscriptionId] && !getters.checkStatus.isPendingDowngrade
		},

		canUseFeature: (state, getters) => (feature) => {
			switch (feature) {
				case 'upgrade':
					return getters.canUpgrade
				case 'pause':
					return getters.canPause
				case 'resume':
					return getters.canResume
				case 'purchaseCancelOffer':
					return getters.canPurchaseCancelOffer
				case 'downgrade':
					return getters.canDowngrade
				default:
					return false
			}
		},
	},

	actions: {
		async fetchSubscription({ commit, getters }, { customer_id }) {
			try {
				const response = await subscriptionApiAxiosInstance.get(`/billing/v2/users/${customer_id}/subscription`, {
					timeout: 5000,
				})
				commit('setSubscription', response.data)
			} catch (error) {
				commit('setSubscription', null)
			}

			return getters.getSubscription
		},
	},
}
